"use client";

import { ComponentPropsWithoutRef, FC } from "react";
import { useMessages } from "../context/i18n";
import { home as enHome, type HomeCopy } from "../dictionaries/en/home";
import { trial as enTrial } from "../dictionaries/en/trial";
import { useInViewVideo } from "../hooks/useInViewVideo";
import EmptyCaptionsTrack from "./EmptyCaptionsTrack";
import CompanyLogosMarquee from "./CompanyLogosMarquee";
import ShortsStack from "./ShortsStack";
import TrustedBy from "./TrustedBy";
import Typography from "./Typography";
import VideoInput from "./VideoInput";

type HeroProps = {
  usersCount?: number;
  copy?: HomeCopy["hero"];
} & ComponentPropsWithoutRef<"section">;

const Hero: FC<HeroProps> = ({ usersCount, copy = enHome.hero, ...rest }) => {
  const heroVideoRef = useInViewVideo<HTMLVideoElement>();
  const trial = useMessages("trial", enTrial);

  return (
    <section {...rest} className={`${rest.className} mx-auto text-center`}>
      <div className="mb-6 mx-auto items-center justify-center flex">
        <TrustedBy usersCount={usersCount} />
      </div>

      {/* The H1 is text only, so crawlers and LLMs read "Turn videos into viral
          shorts". The video chip and the shorts stack show between its words but
          sit outside it: `contents` makes the h1's spans items of this flex
          container, and `order` interleaves them. */}
      <Typography
        variant="h1"
        component="div"
        className="flex items-center justify-center flex-wrap whitespace-pre-wrap overflow-hidden mx-3 leading-[1.2]"
      >
        <h1 className="contents">
          <span className="order-1">{copy.turnVideos}</span>{" "}
          <span className="order-3">{copy.into}</span>{" "}
          <span className="order-5">{copy.viral}</span>{" "}
          <span className="order-7 text-violet-500 transition-opacity">
            {copy.shorts}
          </span>
        </h1>
        {/* Inlined `PreviewCard` to keep the home page out of the editor
            chunk (4435). PreviewCard.tsx is shared with editor surfaces and
            webpack groups it with the player/editor bundle. */}
        {/* Purely decorative visual (the `pointer-events-none` makes it
            non-interactive). No `role` / `cursor-pointer` — both would lie to
            AT and trip Lighthouse a11y. */}
        <div
          aria-hidden="true"
          className="order-2 h-16 aspect-video mx-4 pointer-events-none flex flex-col"
        >
          <div className="aspect-video isolate overflow-hidden relative text-white flex items-center justify-center group bg-slate-200 dark:bg-surface-secondary rounded-md">
            {/* Lighthouse `image-delivery-insight` flags this poster
                (640x360 served, ~228x128 displayed). Until a smaller
                variant is uploaded to assets.klap.app — e.g.
                `poster_16_9_emma_320.jpg` — we keep the existing URL but
                set explicit `width`/`height` so the browser can
                short-circuit layout. `preload="none"` defers the *video*
                bytes (mp4) — most browsers fetch the poster JPEG at
                layout time regardless. The hero is above the fold so
                this is acceptable; the proper fix is a sized poster on
                the CDN. */}
            <video
              ref={heroVideoRef}
              width={228}
              height={128}
              poster="https://assets.klap.app/web-assets/poster_16%3A9_emma.jpg"
              src="https://assets.klap.app/web-assets/360_16_9_emma_compressed.mp4"
              muted
              loop
              playsInline
              preload="none"
            >
              <EmptyCaptionsTrack />
            </video>
            <div className="absolute inset-x-0 top-0 p-1 flex items-center justify-end min-w-0">
              <div className="h-8 to-transparent absolute w-full inset-x-0 pointer-events-none bg-gradient-to-b from-black/30 top-0" />
              <div className="min-w-0 max-w-full px-1.5 py-0.5 bg-white/10 backdrop-blur-md rounded-full text-white flex items-center justify-center z-10">
                {/* Drawn by CSS so no text extractor reads the duration. */}
                <Typography
                  variant="overline"
                  data-duration="2h49"
                  className="block truncate min-w-0 before:content-[attr(data-duration)]"
                />
              </div>
            </div>
          </div>
        </div>
        <div aria-hidden="true" className="order-4 basis-full h-0" />
        <div aria-hidden="true" className="order-6 relative z-10">
          <ShortsStack className="mx-3" />
        </div>
      </Typography>

      <Typography
        className="mt-2 text-slate-500"
        variant="body1"
        component="h2"
      >
        {copy.subtitle}
      </Typography>

      <VideoInput className="mt-8 w-full shadow-2xl max-w-lg mx-auto" />

      <Typography variant="caption" className="text-slate-400 mt-4">
        {trial.cta}
      </Typography>
      <CompanyLogosMarquee className="mt-20" />
      {/* <div className="fill-slate-400 my-28">
        <Typography variant="subtitle1" className="mb-8 block text-slate-500">
          Powered by
        </Typography>
        <div className="flex items-center gap-20 flex-wrap">
          <ReactSVG className="flex-1 w-[200px]" src="/svg/TikTok_logo.svg" />
          <ReactSVG className="flex-1 w-[200px]" src="/svg/OpenAI_logo.svg" />
          <ReactSVG
            className="flex-1 w-[200px]"
            src="/svg/YouTube_dark_logo_(2017).svg"
          />
          <ReactSVG className="flex-1 w-[200px]" src="/svg/Klap_logo.svg" />
          <ReactSVG className="flex-1 w-[200px]" src="/svg/TikTok_logo.svg" />
          <ReactSVG className="flex-1 w-[200px]" src="/svg/OpenAI_logo.svg" />
        </div>
      </div> */}
    </section>
  );
};

export default Hero;
